/**
 * DocumentsFilterToolbar — disposición de filtros de /sales/documents al estilo
 * del mockup de la Vista 360, REUSANDO los controles estándar del sistema
 * (FilterControl: search/select/segment/date-range), para mantener la
 * consistencia con el resto de Clobi.
 *
 *  Fila 1: buscador (avanzado: folio/RUT/razón/monto) · Estado · Cobranza ·
 *          (derecha) Tipo · Vendedor · Más filtros
 *  Fila 2: Fecha (presets + rango)
 *  Avanzados (inline, al expandir "Más filtros"): Personalizados
 *
 * El buscador asume la búsqueda por monto (operadores > / < / = ), por eso ya
 * no hay filtros de "Monto total" ni "Contable" en el bloque avanzado, y
 * Estado/Cobranza suben a la fila 1 para ocupar el espacio liberado.
 *
 * El panel lateral (FilterDrawer) se reemplaza por este despliegue inline.
 * Cambio acotado a esta pantalla; se replicará al resto más adelante.
 */
import { X } from 'lucide-react'
import { FilterControl, type FilterConfig, type useFilters } from '@/components/filters'

interface Props {
  config: FilterConfig
  filters: ReturnType<typeof useFilters>
  /** Si los filtros avanzados están desplegados. El toggle "Más filtros" vive en
   *  el header de la página (junto a Plantillas/Nuevo doc), no en esta barra. */
  advancedOpen?: boolean
  /** Filtros adicionales a medida (p. ej. Personalizados) que van en el bloque inline. */
  advancedChildren?: React.ReactNode
}

export function DocumentsFilterToolbar({ config, filters, advancedOpen = false, advancedChildren }: Props) {
  const byKey = (k: string) => config.find(f => f.key === k)
  const advancedDefs = config.filter(f => f.placement === 'advanced')

  // stacked: rótulo SOBRE el control (compacta la fila y unifica el look con
  // /purchases/invoices). `search` no lleva rótulo arriba.
  const ctrl = (key: string) => {
    const def = byKey(key)
    return def ? <FilterControl def={def} filters={filters} stacked /> : null
  }

  return (
    <div className="bg-glass backdrop-blur-glass border border-divider rounded-card shadow-sm p-4 mb-4">
      {/* Barra tipo "flow": todos los filtros fluyen y envuelven en un solo
          contenedor. El buscador crece (grow) para absorber el espacio sobrante
          y evitar huecos. Al abrir "Más filtros", los avanzados se insertan en el
          MISMO flujo (no en una franja aparte): caen en la fila siguiente si hay
          espacio, o más abajo solo si no caben. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px] flex">{ctrl('search')}</div>
        {ctrl('status')}
        {ctrl('collection_status')}
        {ctrl('type')}
        {ctrl('seller_id')}
        {ctrl('date')}

        {/* Avanzados en el mismo flujo (sin franja aparte). El toggle vive en el header. */}
        {advancedOpen && advancedDefs.map(def => (
          <FilterControl key={def.key} def={def} filters={filters} stacked />
        ))}
        {advancedOpen && advancedChildren}

        {filters.hasActiveFilters && (
          <button
            onClick={filters.resetAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-content-muted border border-divider rounded-lg hover:bg-soft hover:text-danger-content-soft"
          >
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {filters.searchOverridesDateRange && (
        <div className="mt-2 text-xs text-content-muted">
          Buscando en <strong className="font-medium text-content">todo el historial</strong> — elegí un rango de fecha para acotar.
        </div>
      )}
    </div>
  )
}
